/**
 * Client side of the "Quote Requests" feed — `GET /api/v1/quotes/enquiries/`.
 *
 * ONE list over TWO backend tables (see `backend/apps/quotes/enquiries.py`):
 *
 *  * `customer_quote`    — a `quotes.QuoteRequest` raised by a logged-in
 *    CUSTOMER account of the broker (the /buy/:type self-serve flow). Opens at
 *    `/quotes/:id`, the same detail screen the All Quotes tab uses.
 *  * `microsite_enquiry` — a `leads.Lead` created by the public microsite
 *    "request premium" form. It is still a Contacts lead and still lives at
 *    `/leads/:id`; this feed only surfaces it a second time.
 *
 * The endpoint is READ-ONLY on the server and there is deliberately no mutation
 * helper here. Every row carries the same key set whichever table it came from,
 * with `source` as the discriminator and `detail_kind` + `id` as the durable
 * link back to the real record.
 *
 * This module lives under `pages/quotes/` rather than `services/` + `hooks/`
 * because those directories are outside this change's file domain; it uses the
 * shared axios instance so auth, refresh and throttling behave identically.
 */

import { useQuery } from '@tanstack/react-query';

import api from '@/services/api';

export const ENQUIRY_SOURCE_CUSTOMER = 'customer_quote';
export const ENQUIRY_SOURCE_MICROSITE = 'microsite_enquiry';

export type QuoteEnquirySource =
  | typeof ENQUIRY_SOURCE_CUSTOMER
  | typeof ENQUIRY_SOURCE_MICROSITE;

/** Fallback labels. The server sends `source_display`; these only cover a row
 *  that arrives without one (or a source this build has not heard of). */
export const ENQUIRY_SOURCE_LABELS: Record<string, string> = {
  [ENQUIRY_SOURCE_CUSTOMER]: 'Customer portal',
  [ENQUIRY_SOURCE_MICROSITE]: 'Public website',
};

/**
 * Roles the endpoint accepts — `IsAuthenticated, (IsBrokerEmployee | IsPOSP)`
 * in `QuoteEnquiryViewSet`. `insurer_admin` / `insurer_employee` can reach the
 * Quotes page but would get a 403 here, so the tab is hidden from them rather
 * than shown broken.
 */
/*
 * super_admin and country_admin are deliberately ABSENT even though the endpoint
 * accepts them: every row in this tab links to `/quotes/:id` or `/leads/:id`
 * (App.tsx:241, 252), and both routes exclude those two roles. Offering them a
 * tab where each row lands on /unauthorized is worse than not offering it. The
 * API stays open to them; only the tab is hidden.
 */
export const ENQUIRY_TAB_ROLES = [
  'broker_admin',
  'broker_employee',
  'posp',
  'super_posp',
];

export function canSeeQuoteEnquiries(role?: string | null): boolean {
  return !!role && ENQUIRY_TAB_ROLES.includes(role);
}

/** One row of the feed. Mirrors `QuoteEnquirySerializer`. */
export interface QuoteEnquiryRow {
  /** `id` collides across the two tables — key lists on this. */
  row_key: string;
  id: string;
  source: QuoteEnquirySource | string;
  source_display: string;
  /** Quote request number or lead number. */
  reference: string;
  name: string;
  email: string;
  phone: string;
  insurance_type: string;
  insurance_type_code: string;
  status: string;
  status_display: string;
  created_at: string;
  message: string;
  insurer_preference: string;
  requested_by: string;
  assigned_to: string;
  broker: string | null;
  broker_name: string;
  detail_kind: string;
  detail_path: string;
  /** The underlying record — the QuoteRequest, or the Contacts lead a microsite
   *  enquiry came from. Offered as a link on the detail screen; `detail_path`
   *  is where the row itself opens. */
  source_record_path?: string;
  landing_page: string;
  /**
   * The `customers.Customer` PROFILE id behind a customer-portal request, or
   * null. NOT the login `User` id — those are two different tables here and
   * mixing them up has caused four separate bugs. A microsite lead is not a
   * customer at all, so its rows are always null.
   */
  customer_profile_id?: string | null;
}

export interface QuoteEnquiryFeed {
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  /** Per-source totals, computed before the `source` filter is applied, so the
   *  tab can badge both sources while showing only one of them. */
  source_counts: Record<string, number>;
  results: QuoteEnquiryRow[];
}

export interface QuoteEnquiryFilters {
  search?: string;
  source?: string;
  status?: string;
  insurance_type?: string;
}

/** Query string for the feed. Blank filters are omitted entirely. */
export function buildEnquiryParams(page: number, filters?: QuoteEnquiryFilters): string {
  const params = new URLSearchParams();
  params.append('page', String(page));
  const search = filters?.search?.trim();
  if (search) params.append('search', search);
  if (filters?.source) params.append('source', filters.source);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.insurance_type) params.append('insurance_type', filters.insurance_type);
  return params.toString();
}

/**
 * Where a row opens.
 *
 * `detail_kind` is the contract; `detail_path` is a server-built convenience.
 * Known kinds are mapped here so a row always lands on a route this build
 * actually has. An unknown kind falls back to `detail_path` only when it looks
 * like an in-app absolute path — never an off-site or protocol-relative URL.
 */
export function enquiryHref(
  row: Pick<QuoteEnquiryRow, 'detail_kind' | 'id' | 'detail_path'>
): string | null {
  // Both kinds open on the ONE Quote Request screen. A microsite enquiry used
  // to land on /leads/<id>, so reading a quote request meant a detour through
  // Contacts; the lead stays reachable from there as a link.
  if (row.detail_kind === 'quote_request')
    return row.id ? `/quotes/requests/${row.id}?source=customer_quote` : null;
  if (row.detail_kind === 'lead')
    return row.id ? `/quotes/requests/${row.id}?source=microsite_enquiry` : null;
  const path = row.detail_path ?? '';
  if (/^\/[A-Za-z0-9]/.test(path)) return path;
  return null;
}

/** The Quote Wizard. Deep-linkable with `?customer=<Customer profile id>`. */
const QUOTE_CREATE_PATH = '/quotes/new';

/**
 * Where "Create Quote" on a feed row goes.
 *
 * The wizard already prefills the whole proposer block from
 * `?customer=<id>` (`QuoteWizardPage.tsx:193, 352-382`) — it reads the param,
 * fetches the customer and sets name/phone/email/DOB/gender. So all this feed
 * has to do is hand it the right id.
 *
 * That id is the `customers.Customer` PROFILE id, because the wizard passes it
 * to `useCustomer`. Passing the login `User` id instead silently fetches
 * nothing and the broker retypes everything.
 *
 * A row with no profile — every microsite lead, plus any older portal request
 * that was never linked — still returns the bare wizard rather than no link at
 * all. The broker picks the contact in the Customer step, which is a step they
 * would have to take anyway; hiding the action would only cost them the two
 * clicks to reach the wizard.
 */
export function quoteCreateHref(row: Partial<QuoteEnquiryRow>): string {
  const customerId = (row.customer_profile_id || '').trim();
  if (!customerId) return QUOTE_CREATE_PATH;
  // Encoded, not interpolated raw: this value is server-supplied and lands in a
  // query string, so it must not be able to smuggle in a second parameter.
  return `${QUOTE_CREATE_PATH}?customer=${encodeURIComponent(customerId)}`;
}

export const quoteEnquiryService = {
  async list(page: number = 1, filters?: QuoteEnquiryFilters): Promise<QuoteEnquiryFeed> {
    const response = await api.get<QuoteEnquiryFeed>(
      `/quotes/enquiries/?${buildEnquiryParams(page, filters)}`
    );
    return response.data;
  },
};

export function useQuoteEnquiries(
  page: number = 1,
  filters?: QuoteEnquiryFilters,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['quote-enquiries', page, filters],
    queryFn: () => quoteEnquiryService.list(page, filters),
    enabled: options?.enabled ?? true,
    placeholderData: (previousData) => previousData,
  });
}


/** One enquiry, whichever table it came from. */
export interface QuoteEnquiryDetail extends QuoteEnquiryRow {}

export function useQuoteEnquiryDetail(source: string, id: string | undefined) {
  return useQuery({
    queryKey: ['quote-enquiry', source, id],
    queryFn: async () => {
      const { data } = await api.get<QuoteEnquiryDetail>(
        `/quotes/enquiries/${source}/${id}/`,
      );
      return data;
    },
    enabled: Boolean(source && id),
  });
}
